import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userService } from '@/services/user.service';
import { tagSubmissionHistoryService, TagSubmissionHistoryItem } from '@/services/tagSubmissionHistory.service';
import { UserActivity } from '@/types/user.types';
import { GroupedSpecificationItem } from '@/types/groupedHistory.types';

export type HistoryTab = 'all' | 'specification' | 'engagement' | 'tagSubmissions';

export type HistoryItem = UserActivity | TagSubmissionHistoryItem | GroupedSpecificationItem;

export interface TabCounts {
  all: number;
  specification: number;
  engagement: number;
  tagSubmissions: number;
}

export interface HistoryHookResult {
  historyItems: HistoryItem[];
  tabCounts: TabCounts;
  isLoading: boolean;
  error: unknown;
}

export const useHistory = (activeTab: HistoryTab, searchQuery: string): HistoryHookResult => {
  const { data: userData, isLoading: userLoading, error: userError } = useQuery<{ historyActivities: UserActivity[] }, unknown>({
    queryKey: ['user'],
    queryFn: () => userService.getCurrentUser()
  });

  const { data: tagSubmissions, isLoading: tagLoading, error: tagError } = useQuery<TagSubmissionHistoryItem[], unknown>({
    queryKey: ['tagSubmissionHistory'],
    queryFn: () => tagSubmissionHistoryService.getHistoricTagSubmissions(),
    enabled: activeTab === 'tagSubmissions' || activeTab === 'all'
  });

  const isLoading = userLoading || ((activeTab === 'tagSubmissions' || activeTab === 'all') && tagLoading);
  const error = userError || ((activeTab === 'tagSubmissions' || activeTab === 'all') && tagError);

  // Helper function to group specification items by OrderHeader, date, and Transaction Type
  const groupSpecificationItems = (items: UserActivity[]): (UserActivity | GroupedSpecificationItem)[] => {
    if (!items.length) return [];
    
    // Filter only specification items
    const specItems = items.filter(item => item.type === 'specification');
    const nonSpecItems = items.filter(item => item.type !== 'specification');
    
    // Group by date (formatted as YYYY-MM-DD) and subType
    const groupedByDateAndType: Record<string, UserActivity[]> = {};
    
    specItems.forEach(item => {
      const date = new Date(item.date).toISOString().split('T')[0]; // YYYY-MM-DD format
      const subType = item.subType || 'Unknown';
      const orderHeaderId = item.orderHeader?.id || 'null';
      
      // Create a unique key for grouping: date + subType + orderHeaderId
      const key = `${date}_${subType}_${orderHeaderId}`;
      
      if (!groupedByDateAndType[key]) {
        groupedByDateAndType[key] = [];
      }
      
      groupedByDateAndType[key].push(item);
    });
    
    // Convert grouped items to GroupedSpecificationItem
    const result: (UserActivity | GroupedSpecificationItem)[] = [];
    
    // Track running total for points
    let runningTotal = 0;
    
    // Sort keys by date (oldest to newest) to calculate running total correctly
    const sortedKeys = Object.keys(groupedByDateAndType).sort((a, b) => {
      const dateA = a.split('_')[0];
      const dateB = b.split('_')[0];
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
    
    sortedKeys.forEach(key => {
      const items = groupedByDateAndType[key];
      if (items.length === 1) {
        // If there's only one item in the group, keep it as is
        const item = items[0];
        // Update running total
        if (item.points) {
          const pointsValue = item.points.type === 'Debit' ? -item.points.total : item.points.total;
          runningTotal += pointsValue;
          // Add running total to the item
          result.push({
            ...item,
            runningTotal
          } as UserActivity & { runningTotal: number });
        } else {
          result.push(item);
        }
      } else {
        // Group multiple items
        const firstItem = items[0];
        
        //console.log(`Key: ${key} - Items: ${items.length} - First Item: ${items[0].title} - Running Total: ${runningTotal}`);

        // Calculate credits and debits separately
        const creditStandardPoints = items
          .filter(item => item.points?.type === 'Credit')
          .reduce((acc, item) => acc + (item.points?.standard || 0), 0);
        //console.log(`Credit Standard Points: ${creditStandardPoints}`);

        const debitStandardPoints = items
          .filter(item => item.points?.type === 'Debit')
          .reduce((acc, item) => acc + (item.points?.standard || 0), 0);
        //console.log(`Debit Standard Points: ${debitStandardPoints}`);

        const creditBonusPoints = items
          .filter(item => item.points?.type === 'Credit')
          .reduce((acc, item) => acc + (item.points?.bonus || 0), 0);
        //console.log(`Credit Bonus Points: ${creditBonusPoints}`);

        const debitBonusPoints = items
          .filter(item => item.points?.type === 'Debit')
          .reduce((acc, item) => acc + (item.points?.bonus || 0), 0);
        //console.log(`Debit Bonus Points: ${debitBonusPoints}`);

        const creditTotalPoints = items
          .filter(item => item.points?.type === 'Credit')
          .reduce((acc, item) => acc + (item.points?.total || 0), 0);
        //console.log(`Credit Total Points: ${creditTotalPoints}`);

        const debitTotalPoints = items
          .filter(item => item.points?.type === 'Debit')
          .reduce((acc, item) => acc + (item.points?.total || 0), 0);
        //console.log(`Debit Total Points: ${debitTotalPoints}`);

        // Calculate final totals (credits - debits)
        const totalStandardPoints = creditStandardPoints - debitStandardPoints;
        const totalBonusPoints = creditBonusPoints - debitBonusPoints;
        const totalPoints = creditTotalPoints - debitTotalPoints;

        // console.log(`Total Standard Points: ${totalStandardPoints}`);
        // console.log(`Total Bonus Points: ${totalBonusPoints}`);
        // console.log(`Total Points: ${totalPoints}`);

        // Update running total
        runningTotal += totalPoints;
        // console.log(`New Running Total: ${runningTotal}`);
        // console.log('--------------------------------');
        
        // Create grouped item
        const groupedItem: GroupedSpecificationItem = {
          date: firstItem.date,
          type: 'specification',
          subType: firstItem.subType || '',
          orderHeader: firstItem.orderHeader || null,
          items: items,
          points: {
            type: totalPoints >= 0 ? 'Credit' : 'Debit',
            standard: totalStandardPoints,
            bonus: totalBonusPoints,
            total: Math.abs(totalPoints)
          },
          runningTotal
        };
        
        result.push(groupedItem);
      }
    });
    
    // Add back non-specification items
    result.push(...nonSpecItems);
    
    return result;
  };

  const historyItems = useMemo<HistoryItem[]>(() => {
    // Handle tag submissions tab
    if (activeTab === 'tagSubmissions') {
      if (!tagSubmissions) return [];
      
      let filteredItems = [...tagSubmissions];
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredItems = filteredItems.filter((item) =>
          item.clientName?.toLowerCase().includes(query) ||
          item.deliveryAddrStreet?.toLowerCase().includes(query) ||
          item.deliveryAddrCity?.toLowerCase().includes(query) ||
          item.orderNumber?.toLowerCase().includes(query)
        );
      }
      
      // Sort by date from newest to oldest
      return filteredItems.sort((a, b) => 
        new Date(b.lastModifiedDate).getTime() - new Date(a.lastModifiedDate).getTime()
      );
    }

    // Handle regular history items (specification, engagement)
    if (!userData?.historyActivities) return [];
    
    let filtered = [...userData.historyActivities];
    
    if (activeTab !== 'all') {
      filtered = filtered.filter((item) => 
        item.type.toLowerCase() === activeTab
      );
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => 
        item.title.toLowerCase().includes(query) ||
        (item.client?.name?.toLowerCase().includes(query) ?? false) ||
        (item.client?.address?.toLowerCase().includes(query) ?? false) ||
        (item.client?.dealer?.toLowerCase().includes(query) ?? false)
      );
    }
    
    // Group specification items if we're in the specification tab
    if (activeTab === 'specification') {
      const groupedItems = groupSpecificationItems(filtered);
      
      // Calculate running totals in chronological order (oldest to newest)
      const itemsWithTotals = groupedItems.sort((a, b) => {
        const dateA = 'date' in a ? a.date : '';
        const dateB = 'date' in b ? b.date : '';
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      });

      // Reverse the order for display (newest to oldest)
      return itemsWithTotals.reverse();
    }
    
    // For 'all' tab, combine regular history items with tag submissions
    if (activeTab === 'all' && tagSubmissions) {
      // Filter tag submissions if there's a search query
      let filteredTagSubmissions = [...tagSubmissions];
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredTagSubmissions = filteredTagSubmissions.filter((item) =>
          item.clientName?.toLowerCase().includes(query) ||
          item.deliveryAddrStreet?.toLowerCase().includes(query) ||
          item.deliveryAddrCity?.toLowerCase().includes(query) ||
          item.orderNumber?.toLowerCase().includes(query)
        );
      }
      
      // Group specification items before combining
      const groupedItems = groupSpecificationItems(filtered);
      
      // Combine both types of items
      const combinedItems: HistoryItem[] = [
        ...groupedItems,
        ...filteredTagSubmissions
      ];
      
      // Sort all items by date (newest to oldest)
      return combinedItems.sort((a, b) => {
        const dateA = 'date' in a ? a.date : (a as TagSubmissionHistoryItem).lastModifiedDate;
        const dateB = 'date' in b ? b.date : (b as TagSubmissionHistoryItem).lastModifiedDate;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
    }
    
    // Sort by date from newest to oldest (for engagement tab)
    return filtered.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [activeTab, searchQuery, userData?.historyActivities, tagSubmissions]);

  const tabCounts = useMemo<TabCounts>(() => {
    const activities = userData?.historyActivities || [];
    const tagCount = tagSubmissions?.length || 0;
    
    // Get specification items and group them
    const specItems = activities.filter(item => item.type.toLowerCase() === 'specification');
    const groupedByDateAndType: Record<string, UserActivity[]> = {};
    
    // Group specifications by date, subType, and orderHeader
    specItems.forEach(item => {
      const date = new Date(item.date).toISOString().split('T')[0];
      const subType = item.subType || 'Unknown';
      const orderHeaderId = item.orderHeader?.id || 'null';
      const key = `${date}_${subType}_${orderHeaderId}`;
      
      if (!groupedByDateAndType[key]) {
        groupedByDateAndType[key] = [];
      }
      groupedByDateAndType[key].push(item);
    });

    const groupedSpecCount = Object.keys(groupedByDateAndType).length;
    const engagementCount = activities.filter(item => item.type.toLowerCase() === 'engagement').length;

    return {
      all: groupedSpecCount + engagementCount + tagCount,
      specification: groupedSpecCount,
      engagement: engagementCount,
      tagSubmissions: tagCount
    };
  }, [userData?.historyActivities, tagSubmissions]);

  return {
    historyItems,
    tabCounts,
    isLoading,
    error
  };
}
